export const CONVICTION_CHART_INTERVALS = [
  '1s',
  '5s',
  '15s',
  '30s',
  '1m',
  '5m',
  '15m',
  '30m',
  '1h',
  '2h',
  '4h',
  '6h',
  '12h',
  '1d',
] as const;

export type ConvictionChartInterval = (typeof CONVICTION_CHART_INTERVALS)[number];

export const DEFAULT_CONVICTION_CHART_SAMPLES = 360;
export const MIN_CONVICTION_CHART_SAMPLES = 2;
export const MAX_CONVICTION_CHART_SAMPLES = 360;

const INTERVAL_SECONDS: Readonly<Record<ConvictionChartInterval, number>> = {
  '1s': 1,
  '5s': 5,
  '15s': 15,
  '30s': 30,
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1_800,
  '1h': 3_600,
  '2h': 7_200,
  '4h': 14_400,
  '6h': 21_600,
  '12h': 43_200,
  '1d': 86_400,
};

export interface ConvictionChartPageRange {
  readonly from: string;
  readonly to: string;
}

export interface ResolveConvictionChartStartInput {
  readonly tradingStartedAt: string;
  readonly head: string;
  readonly lookbackSeconds?: number;
  readonly viewportLeft?: string;
}

export function convictionChartIntervalSeconds(interval: ConvictionChartInterval): number {
  return INTERVAL_SECONDS[interval];
}

/** The resolution second remains queryable while the painted head stops at resolution. */
export function convictionChartTerminalTo(resolvedAt: string): string {
  const resolvedAtMs = parseIsoTime(resolvedAt, 'resolvedAt');
  return new Date(Math.floor(resolvedAtMs / 1_000) * 1_000 + 1_000).toISOString();
}

export function selectConvictionChartInterval(
  from: string,
  to: string,
  targetSamples = DEFAULT_CONVICTION_CHART_SAMPLES,
): ConvictionChartInterval {
  const fromMs = parseIsoTime(from, 'from');
  const toMs = parseIsoTime(to, 'to');
  if (toMs < fromMs) throw new Error('Conviction chart range ends before it starts');
  validateLimit(targetSamples);
  return (
    CONVICTION_CHART_INTERVALS.find((interval) => {
      const widthMs = convictionChartIntervalSeconds(interval) * 1_000;
      return Math.ceil(toMs / widthMs) - Math.floor(fromMs / widthMs) <= targetSamples;
    }) ?? '1d'
  );
}

export function resolveConvictionChartStart(input: ResolveConvictionChartStartInput): string {
  const tradingStartedAtMs = parseIsoTime(input.tradingStartedAt, 'tradingStartedAt');
  const headMs = parseIsoTime(input.head, 'head');
  // The chart head can precede the chain's trading timestamp during startup or clock skew.
  if (headMs < tradingStartedAtMs) return new Date(headMs).toISOString();
  if (input.viewportLeft !== undefined) {
    return new Date(
      Math.max(tradingStartedAtMs, parseIsoTime(input.viewportLeft, 'viewportLeft')),
    ).toISOString();
  }
  if (input.lookbackSeconds !== undefined) {
    if (!Number.isSafeInteger(input.lookbackSeconds) || input.lookbackSeconds < 0) {
      throw new Error('Conviction chart lookback must be a non-negative integer');
    }
    return new Date(
      Math.max(tradingStartedAtMs, headMs - input.lookbackSeconds * 1_000),
    ).toISOString();
  }
  return new Date(tradingStartedAtMs).toISOString();
}

export function alignConvictionChartTime(
  timestamp: string,
  interval: ConvictionChartInterval,
): string {
  const timestampMs = parseIsoTime(timestamp, 'timestamp');
  const widthMs = convictionChartIntervalSeconds(interval) * 1_000;
  return new Date(Math.floor(timestampMs / widthMs) * widthMs).toISOString();
}

export function planConvictionChartPages(input: {
  readonly from: string;
  readonly to: string;
  readonly interval: ConvictionChartInterval;
  readonly limit?: number;
}): ReadonlyArray<ConvictionChartPageRange> {
  const fromMs = parseIsoTime(input.from, 'from');
  const toMs = parseIsoTime(input.to, 'to');
  if (toMs < fromMs) throw new Error('Conviction chart range ends before it starts');
  const limit = input.limit ?? DEFAULT_CONVICTION_CHART_SAMPLES;
  validateLimit(limit);
  if (fromMs === toMs) return [];

  const widthMs = convictionChartIntervalSeconds(input.interval) * 1_000;
  const pageWidthMs = widthMs * limit;
  const pages: ConvictionChartPageRange[] = [];
  let pageFromMs = Math.floor(fromMs / widthMs) * widthMs;
  while (pageFromMs < toMs) {
    const alignedPageStartMs = Math.floor(pageFromMs / widthMs) * widthMs;
    const pageToMs = Math.min(toMs, alignedPageStartMs + pageWidthMs);
    pages.push({
      from: new Date(pageFromMs).toISOString(),
      to: new Date(pageToMs).toISOString(),
    });
    pageFromMs = pageToMs;
  }
  return pages;
}

/** Advance only the live page on reconnect; closed pages keep their cached history. */
export function convictionChartRecoveryStart(input: {
  readonly previousFrom: string;
  readonly estimatedServerNowMs: number;
  readonly interval: ConvictionChartInterval;
  readonly limit: number;
}): string {
  const bucketMs = convictionChartIntervalSeconds(input.interval) * 1_000;
  const pageMs = bucketMs * input.limit;
  const clockMarginMs = Math.min(5_000, pageMs / 4);
  const fromMs = Math.min(
    Math.floor(input.estimatedServerNowMs / bucketMs) * bucketMs,
    Math.ceil((input.estimatedServerNowMs + clockMarginMs - pageMs) / bucketMs) * bucketMs,
  );
  return new Date(Math.max(parseIsoTime(input.previousFrom, 'previousFrom'), fromMs)).toISOString();
}

function validateLimit(limit: number): void {
  if (
    !Number.isSafeInteger(limit) ||
    limit < MIN_CONVICTION_CHART_SAMPLES ||
    limit > MAX_CONVICTION_CHART_SAMPLES
  ) {
    throw new Error(
      `Conviction chart sample limit must be ${MIN_CONVICTION_CHART_SAMPLES}..${MAX_CONVICTION_CHART_SAMPLES}`,
    );
  }
}

function parseIsoTime(value: string, field: string): number {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) throw new Error(`Invalid conviction chart ${field}`);
  return parsed;
}
